export const VAT_RATE = 0.21;

export const PLANS = [
    {
        id: 'standard',
        name: 'Standard',
        priceRon: 25,
        originalPriceRon: 50,
        vatIncluded: false,
        featured: false,
        perks: [
            '1 QR unic per restaurant',
            'Meniu QR digital — doar vizualizare',
            'Logo & cover personalizat + 2 limbi (RO + 1)',
            'Descarcă & printează QR-ul de meniu',
            'Comenzi, chemare ospătar & funcțiile Pro — blocate',
        ],
    },
    {
        id: 'pro',
        name: 'Pro',
        priceRon: 50,
        originalPriceRon: 119,
        vatIncluded: false,
        featured: true,
        perks: [
            'QR & mese nelimitate',
            'Comenzi online live + KDS & ospătar',
            'Statistici, split bill & branding',
            'Traducere automată + toate limbile',
            'Valori nutriționale generate cu AI',
            'Echipă nelimitată',
            'Toate funcțiile platformei deblocate',
        ],
    },
];

/** Lunar & 3 luni: același preț / lună. 6 luni: −8%. 1 an: −15%. */
export const BILLING_CYCLES = [
    { id: 'monthly', months: 1, label: 'Lunar', discountPercent: 0 },
    { id: 'quarterly', months: 3, label: '3 luni', discountPercent: 0 },
    { id: 'semiannual', months: 6, label: '6 luni', discountPercent: 8 },
    { id: 'annual', months: 12, label: '1 an', discountPercent: 15 },
];

export const LAUNCH_PROMO = {
    planId: 'pro',
    discountPercent: 20,
    durationMonths: 6,
    label: '−20% primele 6 luni',
};

export const PLAN_LABEL = Object.fromEntries(PLANS.map((p) => [p.id, p.name]));

export function planById(id) {
    return PLANS.find((p) => p.id === id) || PLANS[0];
}

export function getBillingCycle(id) {
    return BILLING_CYCLES.find((c) => c.id === id) || BILLING_CYCLES[0];
}

export function billingPeriodTotalLabel(cycleId) {
    const cycle = getBillingCycle(cycleId);
    if (cycle.months === 1) return 'Preț lunar';
    return `Preț per ${cycle.months} luni`;
}

export function roundRon(value) {
    return Math.round(value * 100) / 100;
}

export function formatRon(value) {
    const n = roundRon(value);
    return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.00$/, '');
}

/**
 * Calculează total net, TVA și preț lunar efectiv pentru plan + ciclu facturare.
 */
/**
 * Quote pentru un plan + ciclu de facturare.
 * Acceptă fie ID-uri (string) — caz în care folosește valorile statice — fie obiectele
 * plan/cycle direct, util când vine din /public/plans (cu prețuri override live).
 */
export function planQuote(planOrId, cycleOrId = 'monthly') {
    const plan = typeof planOrId === 'string' ? planById(planOrId) : planOrId;
    const cycle = typeof cycleOrId === 'string' ? getBillingCycle(cycleOrId) : cycleOrId;
    const listMonthly = plan.priceRon;                            // preț de vânzare / lună
    const originalMonthly = plan.originalPriceRon || listMonthly;  // preț „redus de la" (tăiat)
    const months = cycle.months;
    const cycleRate = roundRon(listMonthly * (1 - cycle.discountPercent / 100));

    const periodTotalNet = roundRon(months * cycleRate);
    const displayMonthly = roundRon(periodTotalNet / months);
    const periodVat = roundRon(periodTotalNet * VAT_RATE);
    const periodTotalGross = roundRon(periodTotalNet + periodVat);

    const originalPeriodTotal = roundRon(originalMonthly * months);
    const savingsVsList = roundRon(Math.max(0, originalPeriodTotal - periodTotalNet));
    const savingsPercent = originalPeriodTotal > 0
        ? Math.round((savingsVsList / originalPeriodTotal) * 100)
        : 0;

    return {
        planId: plan.id,
        cycleId: cycle.id,
        listMonthly,
        originalMonthly,
        displayMonthly,
        launchMonthly: null,
        cycleDiscountPercent: cycle.discountPercent,
        showOriginalPrice: originalMonthly > displayMonthly,
        showLaunchPrice: false,
        showLaunchBadge: false,
        periodMonths: months,
        periodTotalNet,
        periodVat,
        periodTotalGross,
        savingsVsList,
        savingsPercent,
    };
}

export function formatPlanPrice(plan, { short = false, cycleId = 'monthly' } = {}) {
    const p = typeof plan === 'string' ? planById(plan) : plan;
    const q = planQuote(p.id, cycleId);
    const suffix = p.vatIncluded ? ' · TVA inclus' : ' + TVA';
    if (short) return `${formatRon(q.displayMonthly)} RON${suffix}`;
    return `${formatRon(q.displayMonthly)} RON${suffix}`;
}

export function planFeatures(restaurant) {
    return restaurant?.plan_features || null;
}

export function hasPlanFeature(restaurant, feature) {
    return Boolean(planFeatures(restaurant)?.features?.[feature]);
}

export function planUsageLabel(restaurant, key) {
    const pf = planFeatures(restaurant);
    if (!pf) return null;
    const used = pf.usage?.[key] ?? 0;
    const max = pf.limits?.[key === 'tables' ? 'max_tables' : key === 'staff' ? 'max_staff' : key];
    if (max == null) return `${used} · nelimitat`;
    return `${used} / ${max}`;
}

export function isAtPlanLimit(restaurant, key) {
    const pf = planFeatures(restaurant);
    if (!pf) return false;
    const used = pf.usage?.[key] ?? 0;
    const maxKey = key === 'tables' ? 'max_tables' : key === 'staff' ? 'max_staff' : key;
    const max = pf.limits?.[maxKey];
    return max != null && used >= max;
}

export function upgradePlanMessage(featureLabel) {
    return `${featureLabel} nu este inclus în planul tău curent. Contactează-ne la contact@clickmeniu.com pentru upgrade.`;
}
